import React from 'react';
import { I18n } from 'react-redux-i18n';
import { parseDate } from '../utils';
import {
    ADELANTO_CUPO_ESTADO,
    formatEstadoCupoLabel,
    isAdelantoQuotaComplete,
    isAdelantoQuotaPending,
    resolveRowEstadoCupo,
} from './adelantoNexState';
import { ADELANTO_CONTRACT_TYPE_ID, VOLUME_CONTRACT_TYPE_ID } from '../constants';

/*
 * R4 — shared cell renderers + filter helper for the Roche and OS Volumen
 * monitor screens (FR-09 / FR-10 / FR-11 / FR-12).
 *
 * Adelanto rows read nested `adelanto_nex` from the volume transaction API:
 *   estado_cupo, saldo_cupo_adelanto, created_at, id, unidades_*, etc.
 * Volumen clásico rows keep using the `volumen` blob (estado_maximo, saldo_cupo).
 */

const EMPTY = '—';

const resolveTipoProceso = (row) => {
    const tipoId =
        row?.contract?.tipo_contrato ?? row?.contrato?.tipo_contrato ?? row?.tipo_contrato;
    if (Number(tipoId) === ADELANTO_CONTRACT_TYPE_ID) return 'C';
    if (Number(tipoId) === VOLUME_CONTRACT_TYPE_ID) return 'V';
    const tipoLetter =
        row?.tipo_proceso ?? row?.contract?.tipo_contrato_tipo ?? row?.contrato?.tipo_contrato_tipo;
    if (tipoLetter === 'V') return 'V';
    return null;
};

/** True when the row belongs to an Adelanto NEX contract (tipo proceso `C`). */
export const isAdelantoRow = (row) => resolveTipoProceso(row) === 'C';

export const TipoProcesoCell = ({ original }) => {
    const t = resolveTipoProceso(original);
    if (t === 'C')
        return (
            <span className='text'>
                {I18n.t('contract.adelantoNex.tipoProcesoLabels.adelanto')}
            </span>
        );
    if (t === 'V')
        return (
            <span className='text'>{I18n.t('contract.adelantoNex.tipoProcesoLabels.volume')}</span>
        );
    return <span className='text'>{EMPTY}</span>;
};

export const VersionCell = ({ original }) => {
    const v = original?.version_numero;
    return <span className='text'>{v != null && v !== '' ? v : EMPTY}</span>;
};

export { formatEstadoCupoLabel };

/*
 * FR-11 — estado cupo label (Pendiente / Parcial / Completo / Excedido),
 * separate from saldo x/y and from the transaction status column.
 */
export const EstadoCupoCell = ({ original }) => {
    if (!isAdelantoRow(original)) return <span className='text'>{EMPTY}</span>;
    const estadoCupo = resolveRowEstadoCupo(original);
    const label = formatEstadoCupoLabel(estadoCupo);
    if (!label) return <span className='text'>{EMPTY}</span>;
    return (
        <span
            className='text'
            style={{ color: original?.adelanto_nex?.estado_color_cupo || 'inherit' }}
        >
            {label}
        </span>
    );
};

/** Saldo cupo x/y for Adelanto rows (e.g. 3/10). */
export const SaldoCupoCell = ({ original }) => {
    if (!isAdelantoRow(original)) {
        return original?.saldo_cupo ? (
            <span className='text' style={{ color: original.estado_color || 'inherit' }}>
                {original.saldo_cupo}
            </span>
        ) : null;
    }
    const saldo = original?.adelanto_nex?.saldo_cupo_adelanto;
    return (
        <span
            className='text'
            style={{ color: original?.adelanto_nex?.estado_color_cupo || 'inherit' }}
        >
            {saldo != null && saldo !== '' ? saldo : EMPTY}
        </span>
    );
};

export const FechaAdelantoCell = ({ original }) => {
    const iso = original?.adelanto_nex?.created_at ?? original?.adelanto_created_at;
    if (!iso) return <span className='text'>{EMPTY}</span>;
    return <span className='text'>{parseDate(new Date(iso))}</span>;
};

export const IdAdelantoCell = ({ original }) => {
    const id = original?.adelanto_nex?.id ?? original?.adelanto_id;
    return <span className='text'>{id != null && id !== '' ? id : EMPTY}</span>;
};

/*
 * FR-12 — client-side filter applier.
 *
 * Filter state shape (defaults to "all" / empty):
 *   {
 *     tipoProceso:     'all' | 'V' | 'C',
 *     estadoCupo:      'all' | PENDIENTE | PARCIAL | COMPLETO | EXCEDIDO,
 *     version:         '' | <string/number>,
 *     estadoRendicion: 'all' | 'pending' | 'complete',
 *   }
 */
export const DEFAULT_ADELANTO_FILTERS = {
    tipoProceso: 'all',
    estadoCupo: 'all',
    version: '',
    estadoRendicion: 'all',
};

export const applyAdelantoFilters = (rows, filters) => {
    if (!Array.isArray(rows)) return rows;
    const f = { ...DEFAULT_ADELANTO_FILTERS, ...(filters ?? {}) };
    const isAllDefault =
        f.tipoProceso === 'all' &&
        f.estadoCupo === 'all' &&
        !f.version &&
        f.estadoRendicion === 'all';
    if (isAllDefault) return rows;

    return rows.filter((row) => {
        if (f.tipoProceso !== 'all') {
            if (resolveTipoProceso(row) !== f.tipoProceso) return false;
        }

        if (f.estadoCupo !== 'all') {
            if (resolveRowEstadoCupo(row) !== f.estadoCupo) return false;
        }

        if (f.version) {
            const v = row?.version_numero;
            if (String(v ?? '') !== String(f.version)) return false;
        }

        if (f.estadoRendicion !== 'all') {
            const estadoCupo = resolveRowEstadoCupo(row);
            if (f.estadoRendicion === 'pending' && !isAdelantoQuotaPending(estadoCupo)) {
                return false;
            }
            if (f.estadoRendicion === 'complete' && !isAdelantoQuotaComplete(estadoCupo)) {
                return false;
            }
        }

        return true;
    });
};

export const AdelantoFilterBar = ({ filters, onChange }) => {
    const f = { ...DEFAULT_ADELANTO_FILTERS, ...(filters ?? {}) };
    const set = (key, value) => onChange({ ...f, [key]: value });

    return (
        <div className='adelanto-filter-bar'>
            <select
                className='adelanto-filter-bar__select'
                value={f.tipoProceso}
                onChange={(e) => set('tipoProceso', e.target.value)}
                aria-label={I18n.t('contract.adelantoNex.filters.processType')}
            >
                <option value='all'>
                    {I18n.t('contract.adelantoNex.filters.processType')} —{' '}
                    {I18n.t('contract.adelantoNex.filters.options.all')}
                </option>
                <option value='V'>{I18n.t('contract.adelantoNex.tipoProcesoLabels.volume')}</option>
                <option value='C'>
                    {I18n.t('contract.adelantoNex.tipoProcesoLabels.adelanto')}
                </option>
            </select>

            <select
                className='adelanto-filter-bar__select'
                value={f.estadoCupo}
                onChange={(e) => set('estadoCupo', e.target.value)}
                aria-label={I18n.t('contract.adelantoNex.filters.statusQuota')}
            >
                <option value='all'>
                    {I18n.t('contract.adelantoNex.filters.statusQuota')} —{' '}
                    {I18n.t('contract.adelantoNex.filters.options.all')}
                </option>
                <option value={ADELANTO_CUPO_ESTADO.PENDIENTE}>
                    {I18n.t('contract.adelantoNex.estadoCupo.pendiente')}
                </option>
                <option value={ADELANTO_CUPO_ESTADO.PARCIAL}>
                    {I18n.t('contract.adelantoNex.estadoCupo.parcial')}
                </option>
                <option value={ADELANTO_CUPO_ESTADO.COMPLETO}>
                    {I18n.t('contract.adelantoNex.estadoCupo.completo')}
                </option>
                <option value={ADELANTO_CUPO_ESTADO.EXCEDIDO}>
                    {I18n.t('contract.adelantoNex.estadoCupo.excedido')}
                </option>
            </select>

            <input
                className='adelanto-filter-bar__input'
                type='text'
                inputMode='numeric'
                placeholder={I18n.t('contract.adelantoNex.filters.version')}
                value={f.version}
                onChange={(e) => set('version', e.target.value)}
            />

            <select
                className='adelanto-filter-bar__select'
                value={f.estadoRendicion}
                onChange={(e) => set('estadoRendicion', e.target.value)}
                aria-label={I18n.t('contract.adelantoNex.filters.quotaStatus')}
            >
                <option value='all'>
                    {I18n.t('contract.adelantoNex.filters.quotaStatus')} —{' '}
                    {I18n.t('contract.adelantoNex.filters.options.all')}
                </option>
                <option value='pending'>
                    {I18n.t('contract.adelantoNex.filters.options.pending')}
                </option>
                <option value='complete'>
                    {I18n.t('contract.adelantoNex.filters.options.complete')}
                </option>
            </select>
        </div>
    );
};
